import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Loader2, Plus, MoreHorizontal, Trash2, Key, Copy, AlertTriangle, CheckCircle2,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/page-header';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { useApiTokens, useCreateToken, useRevokeToken, type ApiToken, type CreateTokenResult } from '@/hooks/use-api-tokens';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';
import { relativeTime, absoluteTime, shortId, cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/tokens')({
  component: TokensPage,
});

// Permissions exposed in the UI for token scope selection. Mirrors the
// server-side ALL_PERMISSIONS list, grouped for readability.
const SCOPE_GROUPS: { label: string; scopes: { id: string; label: string }[] }[] = [
  {
    label: 'Devices',
    scopes: [
      { id: 'device:read', label: 'Read devices' },
      { id: 'device:write', label: 'Write devices' },
      { id: 'device:delete', label: 'Delete devices' },
    ],
  },
  {
    label: 'Sessions',
    scopes: [
      { id: 'session:read', label: 'Read sessions' },
      { id: 'session:write', label: 'Write sessions' },
      { id: 'session:end', label: 'End sessions' },
    ],
  },
  {
    label: 'Users & groups',
    scopes: [
      { id: 'user:read', label: 'Read users' },
      { id: 'group:read', label: 'Read groups' },
    ],
  },
  {
    label: 'Audit',
    scopes: [
      { id: 'audit:read', label: 'Read audit log' },
      { id: 'audit:export', label: 'Export audit log' },
    ],
  },
  {
    label: 'Exports',
    scopes: [
      { id: 'export:devices', label: 'Export devices CSV' },
      { id: 'export:sessions', label: 'Export sessions CSV' },
      { id: 'export:users', label: 'Export users CSV' },
    ],
  },
];

function TokensPage() {
  const { data, isLoading } = useApiTokens();
  const [creating, setCreating] = React.useState(false);
  const [newlyCreated, setNewlyCreated] = React.useState<CreateTokenResult | null>(null);

  return (
    <>
      <PageHeader
        title="API tokens"
        description="Programmatic access for integrations. Tokens are scoped — they can do less than the user who created them, never more."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            <span>New token</span>
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Prefix</TableHead>
              <TableHead>Scopes</TableHead>
              <TableHead>Last used</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            ) : (data?.data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                  <Key className="mx-auto h-5 w-5 mb-2 opacity-50" />
                  No tokens yet. Create one to enable programmatic access.
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((t) => <TokenRow key={t.id} token={t} />)
            )}
          </TableBody>
        </Table>
      </Card>

      <CreateTokenDialog
        open={creating}
        onOpenChange={(o) => {
          if (!o) setCreating(false);
        }}
        onCreated={(result) => {
          setCreating(false);
          setNewlyCreated(result);
        }}
      />

      <NewlyCreatedDialog
        token={newlyCreated}
        onOpenChange={(o) => {
          if (!o) setNewlyCreated(null);
        }}
      />
    </>
  );
}

function TokenRow({ token }: { token: ApiToken }) {
  const revoke = useRevokeToken();

  function handleRevoke(): void {
    if (!window.confirm(`Revoke token "${token.name}"? This cannot be undone, and any integration using it will stop working.`)) return;
    revoke.mutate(token.id, {
      onSuccess: () => toast({ title: 'Token revoked' }),
      onError: (err) => toast({
        variant: 'destructive',
        title: 'Could not revoke',
        description: err instanceof ApiError ? err.message : 'Try again',
      }),
    });
  }

  const isInactive = !!token.revokedAt || token.expired;

  return (
    <TableRow className={cn(isInactive && 'opacity-50')}>
      <TableCell>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{token.name}</span>
          <span className="text-2xs text-muted-foreground">
            Created {relativeTime(token.createdAt)}
            {token.expiresAt && ` · expires ${absoluteTime(token.expiresAt).slice(0, 10)}`}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <span className="id-pill">{token.prefix}…</span>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1 max-w-md">
          {token.scopes.slice(0, 4).map((s) => (
            <Badge key={s} variant="outline" className="font-mono text-2xs">
              {s}
            </Badge>
          ))}
          {token.scopes.length > 4 && (
            <Badge variant="outline" className="font-mono text-2xs">
              +{token.scopes.length - 4}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground tabular-nums">
        {token.lastUsedAt ? relativeTime(token.lastUsedAt) : 'Never'}
      </TableCell>
      <TableCell>
        {token.revokedAt ? (
          <Badge variant="destructive">Revoked</Badge>
        ) : token.expired ? (
          <Badge variant="warning">Expired</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        )}
      </TableCell>
      <TableCell>
        {!isInactive && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon"><MoreHorizontal /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuItem destructive onSelect={handleRevoke}>
                <Trash2 /><span>Revoke</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}

const createSchema = z.object({
  name: z.string().min(1, 'Required').max(100),
  scopes: z.array(z.string()).min(1, 'Select at least one scope'),
  expiresInDays: z.string(),
});
type CreateValues = z.infer<typeof createSchema>;

function CreateTokenDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (result: CreateTokenResult) => void;
}) {
  const create = useCreateToken();
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', scopes: [], expiresInDays: '90' },
  });

  React.useEffect(() => {
    if (open) form.reset({ name: '', scopes: [], expiresInDays: '90' });
  }, [open, form]);

  function toggleScope(id: string): void {
    const current = form.getValues('scopes');
    form.setValue(
      'scopes',
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
      { shouldValidate: true },
    );
  }

  function onSubmit(values: CreateValues): void {
    const days = parseInt(values.expiresInDays, 10);
    const expiresAt = days > 0
      ? new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
      : null;

    create.mutate(
      { name: values.name, scopes: values.scopes, expiresAt },
      {
        onSuccess: (result) => onCreated(result),
        onError: (err) => toast({
          variant: 'destructive',
          title: 'Could not create token',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
      },
    );
  }

  const selectedScopes = form.watch('scopes');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New API token</DialogTitle>
          <DialogDescription>
            The token will be shown only once after creation. Store it in a secret manager.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="t-name">Token name</Label>
            <Input
              id="t-name"
              placeholder="CI deploys, monitoring agent, n8n integration…"
              autoFocus
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Expiration</Label>
            <select
              {...form.register('expiresInDays')}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="365">1 year</option>
              <option value="0">Never (not recommended)</option>
            </select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Scopes</Label>
              <span className="font-mono text-2xs text-muted-foreground">
                {selectedScopes.length} selected
              </span>
            </div>
            {form.formState.errors.scopes && (
              <p className="text-xs text-destructive">{form.formState.errors.scopes.message}</p>
            )}
            <div className="max-h-72 overflow-y-auto space-y-4 border rounded-md p-3 bg-muted/20">
              {SCOPE_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="font-mono text-2xs uppercase tracking-wider text-muted-foreground mb-1.5">
                    {group.label}
                  </p>
                  <div className="space-y-1">
                    {group.scopes.map((scope) => (
                      <label
                        key={scope.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-accent/50 rounded px-2 py-1 -mx-2"
                      >
                        <input
                          type="checkbox"
                          checked={selectedScopes.includes(scope.id)}
                          onChange={() => toggleScope(scope.id)}
                          className="h-3.5 w-3.5 rounded border-input"
                        />
                        <span className="text-sm">{scope.label}</span>
                        <span className="font-mono text-2xs text-muted-foreground ml-auto">{scope.id}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="animate-spin" />}
              Create token
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewlyCreatedDialog({
  token, onOpenChange,
}: {
  token: CreateTokenResult | null;
  onOpenChange: (o: boolean) => void;
}) {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (token) setCopied(false);
  }, [token]);

  if (!token) return null;

  function copy(): void {
    navigator.clipboard.writeText(token!.rawToken).then(() => {
      setCopied(true);
      toast({ title: 'Token copied to clipboard' });
    });
  }

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Token created — copy it now</DialogTitle>
          <DialogDescription>
            This is the only time you'll see the full token. We only store a hash.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <p className="text-xs text-warning-foreground/90">
              Anyone with this token can act on your behalf within the selected scopes.
              Store it in a secret manager — never commit it to git.
            </p>
          </div>

          <div>
            <Label className="mb-2 block">Token</Label>
            <div className="flex gap-2">
              <code className="flex-1 rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs break-all">
                {token.rawToken}
              </code>
              <Button onClick={copy} variant={copied ? 'outline' : 'default'} size="default">
                {copied ? <CheckCircle2 /> : <Copy />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </Button>
            </div>
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              <span className="font-mono">Authorization: Bearer {token.prefix}…</span>
            </p>
            <p>Use this header on every request to the WeRemote API.</p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
